/* ═══════════════════════════════════════════════════════════════════════════════
   WATRLY WATER ICONS 💧
   أيقونات مائية مذهلة ومتنوعة — Water Icons Component
   ═══════════════════════════════════════════════════════════════════════════════ */

import React from 'react';

// ═══════════════════════════════════════════════════════════════════════════════
// Main Water Icon Component — المكون الرئيسي للأيقونات المائية
// ═══════════════════════════════════════════════════════════════════════════════
export const WaterIcon = ({ 
  children, 
  size = 'md', 
  variant = 'primary', 
  animated = false,
  glow = false,
  className = ''
}) => {
  const sizeClasses = {
    sm: 'w-9 h-9',
    md: 'w-12 h-12',
    lg: 'w-16 h-16',
    xl: 'w-20 h-20'
  };

  const iconSizes = {
    sm: 'w-[18px] h-[18px]',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-10 h-10'
  };

  const variantClasses = {
    primary: 'bg-gradient-to-br from-[rgba(10,186,181,0.15)] to-[rgba(0,188,212,0.1)] border-[rgba(10,186,181,0.25)] text-[#0ABAB5]',
    ocean: 'bg-gradient-to-br from-[rgba(0,102,204,0.15)] to-[rgba(0,188,212,0.1)] border-[rgba(0,102,204,0.25)] text-[#0066CC]',
    fresh: 'bg-gradient-to-br from-[rgba(0,212,170,0.15)] to-[rgba(10,186,181,0.1)] border-[rgba(0,212,170,0.25)] text-[#00D4AA]',
    wave: 'bg-gradient-to-br from-[rgba(41,182,246,0.15)] to-[rgba(0,188,212,0.1)] border-[rgba(41,182,246,0.25)] text-[#29B6F6]',
    glass: 'bg-[rgba(255,255,255,0.08)] border-[rgba(255,255,255,0.15)] text-white'
  };

  const glowClass = glow ? 'shadow-[0_0_20px_rgba(10,186,181,0.4)]' : '';
  const animatedClass = animated ? 'animate-iconFloat' : '';

  return (
    <div 
      className={`
        flex items-center justify-center rounded-xl 
        border backdrop-blur-sm
        transition-all duration-300 ease-out
        hover:scale-110 hover:shadow-lg
        ${sizeClasses[size]}
        ${variantClasses[variant]}
        ${glowClass}
        ${animatedClass}
        ${className}
      `}
      style={{
        backgroundImage: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.2) 0%, transparent 50%)'
      }}
    >
      <div className={iconSizes[size]}>
        {children}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Individual Icon Components — مكونات الأيقونات الفردية
// ═══════════════════════════════════════════════════════════════════════════════

// 💧 Droplet Icon — أيقونة القطرة
export const DropletIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
    <path 
      d="M12 5c-3 3-5 5.5-5 8.5a5 5 0 0 0 10 0C17 10.5 15 8 12 5z" 
      fill="rgba(255,255,255,0.3)"
    />
    <ellipse cx="10" cy="9" rx="2" ry="3" fill="rgba(255,255,255,0.4)" transform="rotate(-30 10 9)" />
  </svg>
);

// 🌊 Wave Icon — أيقونة الموجة
export const WaveIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
    <path strokeLinecap="round" d="M2 12c2-3 4-5 6-5s4 2 6 2 4-4 6-4 4 4 4 4" />
    <path strokeLinecap="round" d="M2 17c2-3 4-5 6-5s4 2 6 2 4-4 6-4 4 4 4 4" opacity="0.5" />
    <path strokeLinecap="round" d="M2 7c2-3 4-5 6-5s4 2 6 2 4-4 6-4 4 4 4 4" opacity="0.3" />
  </svg>
);

// 🚚 Water Truck Icon — أيقونة شاحنة المياه
export const WaterTruckIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
    <path strokeLinecap="round" d="M10 17h6V7H8v7H6" />
    <path strokeLinecap="round" d="M6 17H4v-3l2-2h2" />
    <circle cx="7" cy="17" r="2" />
    <circle cx="17" cy="17" r="2" />
    <path strokeLinecap="round" d="M9 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <path strokeLinecap="round" d="M12 4v3" opacity="0.5" />
    <path strokeLinecap="round" d="M10 10h4" opacity="0.5" />
  </svg>
);

// 🏠 Home Icon (using home-blue.svg)
export const WaterHomeIcon = ({ className = '', active = false, isDark = true }) => (
  <img
    src="/assets/images/sysIcons/home-blue.svg"
    alt="home"
    className={className}
    style={{
      width: '24px',
      height: '24px',
      filter: active
        ? 'brightness(1.2)'
        : isDark
          ? 'invert(100%) brightness(0.75)'
          : 'invert(30%) brightness(0.6)',
    }}
  />
);

// 📋 List/Orders Icon
export const WaterListIcon = ({ className = '', active = false, isDark = true }) => {
  const color = active ? '#0ABAB5' : isDark ? 'rgba(255,255,255,0.6)' : 'rgba(30,40,60,0.65)';
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/>
      <rect x="9" y="3" width="6" height="4" rx="1"/>
      <path d="M9 12h6M9 16h4"/>
    </svg>
  );
};

// 👤 Profile Icon
export const WaterProfileIcon = ({ className = '', active = false, isDark = true }) => {
  const color = active ? '#0ABAB5' : isDark ? 'rgba(255,255,255,0.6)' : 'rgba(30,40,60,0.65)';
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="4"/>
      <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
    </svg>
  );
};

// 📍 Location/Pin Icon — أيقونة الموقع
export const WaterPinIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
    <path strokeLinecap="round" d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" fill="currentColor" fillOpacity="0.2" />
    <ellipse cx="12" cy="21" rx="4" ry="1.5" fill="currentColor" fillOpacity="0.1" />
  </svg>
);

// 💳 Wallet Icon
export const WaterWalletIcon = ({ className = '', active = false, isDark = true }) => {
  const color = active ? '#0ABAB5' : isDark ? 'rgba(255,255,255,0.6)' : 'rgba(30,40,60,0.65)';
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="5" width="20" height="14" rx="2"/>
      <path d="M16 12a1 1 0 100 2 1 1 0 000-2z" fill={color} stroke="none"/>
      <path d="M2 9h20"/>
    </svg>
  );
};

// ➕ Plus/Add Icon — أيقونة الإضافة
export const WaterPlusIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
    <circle cx="12" cy="12" r="9" strokeOpacity="0.2" />
    <path strokeLinecap="round" d="M12 8v8M8 12h8" />
    <circle cx="12" cy="12" r="3" fill="currentColor" fillOpacity="0.2" />
  </svg>
);

// ✓ Check Icon — أيقونة التأكيد
export const WaterCheckIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className={className}>
    <circle cx="12" cy="12" r="9" strokeWidth="1.5" opacity="0.2" />
    <path strokeLinecap="round" d="M8 12l3 3 5-7" />
  </svg>
);

// ✕ Close Icon — أيقونة الإغلاق
export const WaterCloseIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
    <circle cx="12" cy="12" r="9" strokeWidth="1.5" opacity="0.2" />
    <path strokeLinecap="round" d="M9 9l6 6M15 9l-6 6" />
  </svg>
);

// ‹ Chevron Left Icon
export const WaterChevronLeftIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

// › Chevron Right Icon
export const WaterChevronRightIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M9 18l6-6-6-6" />
  </svg>
);

// 🔍 Search Icon — أيقونة البحث
export const WaterSearchIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
    <circle cx="11" cy="11" r="7" />
    <path strokeLinecap="round" d="M21 21l-4.35-4.35" />
    <path d="M8 11a3 3 0 0 1 3-3" stroke="currentColor" strokeWidth="1" opacity="0.4" />
  </svg>
);

// 🔔 Notification Bell Icon — أيقونة الإشعارات
export const WaterBellIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
    <path strokeLinecap="round" d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path strokeLinecap="round" d="M13.73 21a2 2 0 0 1-3.46 0" />
    <path d="M12 3v1" stroke="currentColor" strokeLinecap="round" />
    <circle cx="18" cy="5" r="2" fill="currentColor" className="animate-pulse" />
  </svg>
);

// ⚙️ Settings Icon — أيقونة الإعدادات
export const WaterSettingsIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
    <circle cx="12" cy="12" r="3" />
    <path strokeLinecap="round" d="M12 1v6m0 6v6m4.22-10.22l4.24-4.24M6.34 17.66l-4.24 4.24M23 12h-6m-6 0H1m20.24 4.24l-4.24-4.24M6.34 6.34L2.1 2.1" />
    <circle cx="12" cy="12" r="8" strokeOpacity="0.2" strokeDasharray="4 2" />
  </svg>
);

// 📱 Chat/Message Icon — أيقونة الدردشة
export const WaterChatIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
    <path strokeLinecap="round" d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7A8.38 8.38 0 0 1 4 11.5a8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5" />
    <path d="M8 12h.01M12 12h.01M16 12h.01" stroke="currentColor" strokeLinecap="round" strokeWidth="2" />
    <circle cx="19" cy="5" r="2" fill="currentColor" />
  </svg>
);

// 🕐 Clock/Time Icon — أيقونة الساعة
export const WaterClockIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
    <circle cx="12" cy="12" r="9" />
    <path strokeLinecap="round" d="M12 7v5l3 3" />
    <path d="M12 3v1M12 20v1M3 12h1M20 12h1" stroke="currentColor" strokeWidth="1" opacity="0.3" />
  </svg>
);

// 📞 Phone/Call Icon — أيقونة الهاتف
export const WaterPhoneIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
    <path strokeLinecap="round" d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    <path d="M16 2c2 2 3 4 3 6" stroke="currentColor" strokeLinecap="round" opacity="0.4" />
  </svg>
);

// 🎯 Target/Location Target Icon — أيقونة الهدف
export const WaterTargetIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="5" />
    <circle cx="12" cy="12" r="2" fill="currentColor" />
    <path d="M12 3v2M12 19v2M3 12h2M19 12h2" stroke="currentColor" strokeWidth="1" opacity="0.4" />
  </svg>
);

// 📦 Package/Box Icon — أيقونة الصندوق
export const WaterPackageIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
    <path strokeLinecap="round" d="M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3z" />
    <path strokeLinecap="round" d="M12 12l8-4.5M12 12v9" />
    <path strokeLinecap="round" d="M12 12L4 7.5" opacity="0.5" />
    <path strokeLinecap="round" d="M20 7.5l-8 4.5" opacity="0.3" />
  </svg>
);

// 🌟 Star/Favorite Icon — أيقونة النجمة
export const WaterStarIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
    <path 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" 
      fill="currentColor"
      fillOpacity="0.2"
    />
  </svg>
);

// 🗺️ Map Icon — أيقونة الخريطة
export const WaterMapIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
    <path strokeLinecap="round" d="M1 6v16l7-4 8 4 7-4V2l-7 4-8-4-7 4z" />
    <path strokeLinecap="round" d="M8 2v16M16 6v16" opacity="0.5" />
    <circle cx="12" cy="10" r="2" fill="currentColor" fillOpacity="0.3" />
  </svg>
);

// 📤 Share Icon — أيقونة المشاركة
export const WaterShareIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
    <circle cx="18" cy="5" r="3" fill="currentColor" fillOpacity="0.2" />
    <circle cx="6" cy="12" r="3" fill="currentColor" fillOpacity="0.2" />
    <circle cx="18" cy="19" r="3" fill="currentColor" fillOpacity="0.2" />
    <path strokeLinecap="round" d="M8.12 9.88l7.76-3.76M8.12 14.12l7.76 3.76" />
  </svg>
);

// 🔄 Refresh/Reload Icon — أيقونة التحديث
export const WaterRefreshIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
    <path strokeLinecap="round" d="M23 4v6h-6" />
    <path strokeLinecap="round" d="M1 20v-6h6" />
    <path strokeLinecap="round" d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    <path d="M12 7v3l2 2" stroke="currentColor" strokeWidth="1" opacity="0.4" />
  </svg>
);

// 🏆 Trophy/Achievement Icon — أيقونة الكأس
export const WaterTrophyIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
    <path strokeLinecap="round" d="M8 21h8M12 17v4" />
    <path strokeLinecap="round" d="M6 3h12v5a6 6 0 0 1-12 0V3z" fill="currentColor" fillOpacity="0.1" />
    <path strokeLinecap="round" d="M6 3v5M18 3v5M6 8h12" />
    <path d="M6 3c0 2-2 3-2 5s2 3 2 3M18 3c0 2 2 3 2 5s-2 3-2 3" stroke="currentColor" strokeWidth="1" opacity="0.4" />
  </svg>
);

// 💰 Money/Coins Icon — أيقونة المال
export const WaterMoneyIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v10M15 10H9.5a2.5 2.5 0 0 0 0 5h5a2.5 2.5 0 0 1 0 5H9" stroke="currentColor" strokeLinecap="round" />
    <circle cx="12" cy="12" r="2" fill="currentColor" fillOpacity="0.3" />
  </svg>
);

// 📊 Chart/Statistics Icon — أيقونة الإحصائيات
export const WaterChartIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
    <path strokeLinecap="round" d="M18 20V10M12 20V4M6 20v-6" />
    <path strokeLinecap="round" d="M3 20h18" opacity="0.3" />
    <rect x="4" y="14" width="4" height="6" rx="1" fill="currentColor" fillOpacity="0.2" />
    <rect x="10" y="8" width="4" height="12" rx="1" fill="currentColor" fillOpacity="0.2" />
    <rect x="16" y="10" width="4" height="10" rx="1" fill="currentColor" fillOpacity="0.2" />
  </svg>
);

// 🔒 Lock/Security Icon — أيقونة القفل
export const WaterLockIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
    <rect x="5" y="11" width="14" height="10" rx="2" fill="currentColor" fillOpacity="0.1" />
    <path strokeLinecap="round" d="M7 11V7a5 5 0 0 1 10 0v4" />
    <circle cx="12" cy="16" r="1" fill="currentColor" />
    <path d="M12 16v2" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

// 🔓 Unlock Icon — أيقونة الفتح
export const WaterUnlockIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
    <rect x="5" y="11" width="14" height="10" rx="2" fill="currentColor" fillOpacity="0.1" />
    <path strokeLinecap="round" d="M7 11V7a5 5 0 0 1 9.9-1" />
    <circle cx="12" cy="16" r="1" fill="currentColor" />
    <path d="M12 16v2" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

// 🌙 Moon/Dark Mode Icon — أيقونة القمر
export const WaterMoonIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
    <path 
      strokeLinecap="round" 
      d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" 
      fill="currentColor"
      fillOpacity="0.1"
    />
    <circle cx="12" cy="12" r="3" fill="currentColor" fillOpacity="0.2" />
  </svg>
);

// ☀️ Sun/Light Mode Icon — أيقونة الشمس
export const WaterSunIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
    <circle cx="12" cy="12" r="5" fill="currentColor" fillOpacity="0.2" />
    <path strokeLinecap="round" d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
  </svg>
);

// 📸 Camera Icon — أيقونة الكاميرا
export const WaterCameraIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
    <path strokeLinecap="round" d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" fill="currentColor" fillOpacity="0.1" />
    <circle cx="12" cy="13" r="2" fill="currentColor" fillOpacity="0.3" />
  </svg>
);

// 🎨 Color/Palette Icon — أيقونة الألوان
export const WaterPaletteIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
    <circle cx="12" cy="12" r="9" fill="currentColor" fillOpacity="0.05" />
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="3" fill="currentColor" />
    <circle cx="17" cy="8" r="1.5" fill="currentColor" fillOpacity="0.5" />
    <circle cx="8" cy="9" r="1.5" fill="currentColor" fillOpacity="0.5" />
    <circle cx="8" cy="16" r="1.5" fill="currentColor" fillOpacity="0.5" />
  </svg>
);

// 🚀 Rocket/Speed Icon — أيقونة الصاروخ
export const WaterRocketIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
    <path strokeLinecap="round" d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" fill="currentColor" fillOpacity="0.1" />
    <path strokeLinecap="round" d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" fill="currentColor" fillOpacity="0.1" />
    <path strokeLinecap="round" d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
    <path d="M15 6l3 3" stroke="currentColor" strokeLinecap="round" opacity="0.5" />
  </svg>
);

// 💡 Lightbulb/Idea Icon — أيقونة الفكرة
export const WaterIdeaIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
    <path strokeLinecap="round" d="M9 18h6M10 22h4" />
    <path strokeLinecap="round" d="M12 2a7 7 0 0 1 7 7c0 2.38-1.19 4.47-3 5.74V17a2 2 0 0 1-2 2H10a2 2 0 0 1-2-2v-2.26C6.19 13.47 5 11.38 5 9a7 7 0 0 1 7-7z" fill="currentColor" fillOpacity="0.1" />
    <path d="M12 6v3" stroke="currentColor" strokeWidth="1" opacity="0.4" />
  </svg>
);

// 🎁 Gift Icon — أيقونة الهدية
export const WaterGiftIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
    <rect x="3" y="8" width="18" height="4" rx="1" fill="currentColor" fillOpacity="0.1" />
    <rect x="3" y="12" width="18" height="9" rx="1" />
    <path strokeLinecap="round" d="M12 8v13M20 8v13M4 8v13" opacity="0.5" />
    <path strokeLinecap="round" d="M12 8c0-1.5-1-3-3-3S6 6.5 6 8h6zM12 8c0-1.5 1-3 3-3s3 1.5 3 3h-6z" fill="currentColor" fillOpacity="0.2" />
  </svg>
);

// 📅 Calendar Icon — أيقونة التقويم
export const WaterCalendarIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
    <rect x="3" y="4" width="18" height="18" rx="2" fill="currentColor" fillOpacity="0.05" />
    <path strokeLinecap="round" d="M16 2v4M8 2v4M3 10h18" />
    <rect x="7" y="14" width="4" height="4" rx="1" fill="currentColor" fillOpacity="0.2" />
    <rect x="13" y="14" width="4" height="4" rx="1" fill="currentColor" fillOpacity="0.2" />
  </svg>
);

// ═══════════════════════════════════════════════════════════════════════════════
// Icon Gallery Export — تصدير معرض الأيقونات
// ═══════════════════════════════════════════════════════════════════════════════
export const WaterIcons = {
  Droplet: DropletIcon,
  Wave: WaveIcon,
  Truck: WaterTruckIcon,
  Home: WaterHomeIcon,
  List: WaterListIcon,
  Profile: WaterProfileIcon,
  Pin: WaterPinIcon,
  Wallet: WaterWalletIcon,
  Plus: WaterPlusIcon,
  Check: WaterCheckIcon,
  Close: WaterCloseIcon,
  Search: WaterSearchIcon,
  Bell: WaterBellIcon,
  Settings: WaterSettingsIcon,
  Chat: WaterChatIcon,
  Clock: WaterClockIcon,
  Phone: WaterPhoneIcon,
  Target: WaterTargetIcon,
  Package: WaterPackageIcon,
  Star: WaterStarIcon,
  Map: WaterMapIcon,
  Share: WaterShareIcon,
  Refresh: WaterRefreshIcon,
  Trophy: WaterTrophyIcon,
  Money: WaterMoneyIcon,
  Chart: WaterChartIcon,
  Lock: WaterLockIcon,
  Unlock: WaterUnlockIcon,
  Moon: WaterMoonIcon,
  Sun: WaterSunIcon,
  Camera: WaterCameraIcon,
  Palette: WaterPaletteIcon,
  Rocket: WaterRocketIcon,
  Idea: WaterIdeaIcon,
  Gift: WaterGiftIcon,
  Calendar: WaterCalendarIcon,
};

export default WaterIcons;
